import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type CategoryManagementRecord = {
  category_management_id: string;
  client_id: string;
  /** 修繕カテゴリ (hardcode デフォルト + カスタム追加) */
  categories: string[];
  /** 設備種別 (hardcode デフォルト + カスタム追加) */
  equipment_categories?: string[];
  // 支出カテゴリは BudgetCategory に統一済み (旧フィールド `expense_categories` は廃止)
  created_at: string;
  updated_at?: string;
};

export const fetchCategoryManagement =
  createAsyncThunk<CategoryManagementRecord | null>(
    "categoryManagement/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await ipponApi.get(`${BaseURL}/api/v1/category-management`);
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        return list.length > 0 ? (list[0] as CategoryManagementRecord) : null;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data || "Failed to fetch category management"
        );
      }
    }
  );

export const createCategoryManagement = createAsyncThunk<
  CategoryManagementRecord,
  {
    categories?: string[];
    equipment_categories?: string[];
  }
>("categoryManagement/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/category-management`, body);
    return res.data.data as CategoryManagementRecord;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data || "Failed to create category management"
    );
  }
});

export const updateCategoryManagement = createAsyncThunk<
  CategoryManagementRecord,
  {
    category_management_id: string;
    categories?: string[];
    equipment_categories?: string[];
  }
>(
  "categoryManagement/update",
  async ({ category_management_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/category-management/${category_management_id}`,
        body
      );
      return res.data.data as CategoryManagementRecord;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update category management"
      );
    }
  }
);

export const upsertCategoryManagement = createAsyncThunk<
  CategoryManagementRecord,
  {
    existingId?: string | null;
    categories?: string[];
    equipment_categories?: string[];
  }
>(
  "categoryManagement/upsert",
  async (
    { existingId, categories, equipment_categories },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const payload: any = {};
      if (categories !== undefined) payload.categories = categories;
      if (equipment_categories !== undefined)
        payload.equipment_categories = equipment_categories;

      if (existingId) {
        const resultAction: any = await dispatch(
          updateCategoryManagement({
            category_management_id: existingId,
            ...payload,
          })
        );
        if (updateCategoryManagement.rejected.match(resultAction)) {
          throw resultAction.payload || new Error("update failed");
        }
        return resultAction.payload as CategoryManagementRecord;
      }
      const resultAction: any = await dispatch(
        createCategoryManagement(payload)
      );
      if (createCategoryManagement.rejected.match(resultAction)) {
        throw resultAction.payload || new Error("create failed");
      }
      return resultAction.payload as CategoryManagementRecord;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

type CategoryManagementState = {
  loading: boolean;
  error: boolean;
  record: CategoryManagementRecord | null;
};

const initialState: CategoryManagementState = {
  loading: false,
  error: false,
  record: null,
};

export const categoryManagementSlice = createSlice({
  name: "categoryManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchCategoryManagement.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchCategoryManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.record = action.payload as any;
    });
    builder.addCase(fetchCategoryManagement.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });

    builder.addCase(createCategoryManagement.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(createCategoryManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.record = action.payload as any;
    });
    builder.addCase(createCategoryManagement.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });

    builder.addCase(updateCategoryManagement.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateCategoryManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.record = action.payload as any;
    });
    builder.addCase(updateCategoryManagement.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });

    builder.addCase(upsertCategoryManagement.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(upsertCategoryManagement.fulfilled, (state, action) => {
      state.loading = false;
      state.record = action.payload as any;
    });
    builder.addCase(upsertCategoryManagement.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export default categoryManagementSlice.reducer;
